import { AsyncLocalStorage } from "node:async_hooks";
import { unscopeTemplateName } from "@brains/content-service";
import type { JsonObject } from "@brains/contracts";
import { createServiceContentTarget } from "./content-generation-target";
import type { JobHandler, JobInfo } from "@brains/job-queue";
import type { ProgressReporter } from "@brains/utils/progress";
import {
  createConfirmationGate,
  type Prompt,
  type Resource,
  type Tool,
  type ToolContext,
  type ToolResponse,
} from "@brains/mcp-service";
import {
  createTemplate,
  type ComponentType,
  type Template,
  type TemplateDataSchema,
} from "@brains/templates";
import { getErrorMessage } from "@brains/utils/error";
import { z } from "@brains/utils/zod";
import { parseWithSchema } from "@brains/utils/parse-schema";
import { emptyPluginState } from "../base/empty-state";
import type {
  PluginCapabilities,
  PluginRegistrationContext,
  IShell,
} from "../interfaces";
import type { AnyAccountSettingsDefinition } from "../operator/account-settings-definition-contract";
import type { AccountSettingsRegistration } from "../operator/account-settings-registry";
import { createDeclarativeStudioWorkspaceRegistration } from "../operator/studio-workspace-runtime";
import { createDeclarativeDashboardWidgetRegistration } from "../operator/dashboard-widget-runtime";
import type {
  AnyStudioWorkspaceDefinition,
  AnyDashboardWidgetDefinition,
  BoundStudioWorkspace,
  BoundDashboardWidget,
} from "../operator/operator-definition-contract";
import {
  identityConfigSchema,
  type InstalledPluginPackageMetadata,
} from "../package-definition";
import { parseDefinitionEntity } from "../entity/declarative-entity-plugin";
import { ServicePlugin } from "./service-plugin";
import type { ServicePluginContext } from "./context";
import type {
  AnyServiceJobDefinition,
  AnyServiceToolDefinition,
  NormalizedServiceDefinitionInput,
  ServiceContentGeneration,
  ServiceContentGenerationResult,
  ServiceJobBinding,
  ServiceJobReference,
  ServiceJobStatus,
  ServiceJobs,
  ServiceResourceDefinition,
  ServiceSchema,
  ServiceSchemaMap,
  ServiceViewSchemaMap,
  ServiceTemplateFormatter,
  ServiceTemplateGenerationDefinition,
  ServiceTemplateShapeMap,
} from "./service-definition-contract";
import {
  getServiceJobHandler,
  parseServiceDeadline,
} from "./service-definition-contract";
import {
  bindServiceJobRuntimeType,
  unbindServiceJobRuntimeType,
} from "./job-definition-runtime";

const confirmationTokenField = "_rizomConfirmationToken";

/** A template with its schema type erased and `format` bound to that schema. */
interface ErasedServiceTemplate {
  readonly schema: ServiceSchema;
  readonly generation?: ServiceTemplateGenerationDefinition | undefined;
  format(value: unknown): string;
}

/**
 * A view with its schema erased to the JSON bound it is declared with, and its
 * author component wrapped so props are parsed before render.
 */
interface ErasedServiceView {
  readonly schema: TemplateDataSchema<JsonObject>;
  readonly description?: string | undefined;
  readonly component: ComponentType<JsonObject>;
}

function toolConfirmationToken(input: unknown): string | undefined {
  if (input === null || typeof input !== "object") return undefined;
  const token = Reflect.get(input, confirmationTokenField);
  return typeof token === "string" ? token : undefined;
}

function promptInput(value: string | undefined): unknown {
  if (value === undefined) return {};
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function statusFor<TDefinition extends AnyServiceJobDefinition>(
  definition: TDefinition,
  job: JobInfo,
): ServiceJobStatus<z.output<TDefinition["output"]>> {
  const result: z.output<TDefinition["output"]> | undefined =
    job.status === "completed" && job.result !== undefined
      ? parseWithSchema<TDefinition["output"]>(definition.output, job.result)
      : undefined;
  return {
    id: job.id,
    status: job.status,
    progress: job.progress,
    ...(result !== undefined ? { result } : {}),
    ...(job.lastError ? { error: job.lastError } : {}),
  };
}

function runtimeJobHandler(
  binding: ServiceJobBinding,
  context: ServicePluginContext,
  templates: ServiceTemplateFormatter,
): JobHandler<string, unknown, unknown> {
  const definition = binding.definition;
  const handler = getServiceJobHandler(binding);
  return {
    ...(definition.deadline
      ? { executionTimeoutMs: parseServiceDeadline(definition.deadline) }
      : {}),
    validateAndParse(data): unknown | null {
      const parsed = definition.input.safeParse(data);
      return parsed.success ? parsed.data : null;
    },
    async process(
      input: unknown,
      _jobId: string,
      progress: ProgressReporter,
      signal: AbortSignal,
    ): Promise<unknown> {
      const output = await handler({
        input,
        signal,
        progress,
        templates,
        entities: {
          async get(entityDefinition, id) {
            const entity = await context.entityService.getEntity({
              entityType: entityDefinition.type,
              id,
              visibilityScope: "restricted",
            });
            return entity
              ? parseDefinitionEntity(entityDefinition, entity)
              : null;
          },
        },
        messaging: {
          async publish(message): Promise<void> {
            await context.messaging.send({
              type: message.topic,
              payload: message.data,
            });
          },
        },
      });
      return definition.output.parse(output);
    },
  };
}

class DeclarativeServicePlugin<
  TConfigSchema extends z.ZodType<object, object>,
  TState extends object,
  TPromptSchemas extends ServiceSchemaMap,
  TTemplateSchemas extends ServiceSchemaMap,
  TViewSchemas extends ServiceViewSchemaMap,
  TAccountSettings extends AnyAccountSettingsDefinition | undefined,
  TTemplateDefinitions extends ServiceTemplateShapeMap,
> extends ServicePlugin<z.output<TConfigSchema>, z.output<TConfigSchema>> {
  private readonly definition: NormalizedServiceDefinitionInput<
    TConfigSchema,
    TState,
    TPromptSchemas,
    TTemplateSchemas,
    TViewSchemas,
    TAccountSettings,
    TTemplateDefinitions
  >;
  private readonly publicId: string;
  private readonly toolContext = new AsyncLocalStorage<ToolContext>();
  private readonly cleanups: Array<() => void | Promise<void>> = [];
  private readonly registeredJobs = new Set<AnyServiceJobDefinition>();
  private readonly operatorAbortController = new AbortController();
  private readonly registeredStudioWorkspaceIds: string[] = [];
  private readonly registeredDashboardWidgetIds: string[] = [];
  private accountSettingsRegistration:
    AccountSettingsRegistration<NonNullable<TAccountSettings>> | undefined;
  private studioWorkspaceBindings: readonly BoundStudioWorkspace<
    AnyStudioWorkspaceDefinition,
    z.output<TConfigSchema>,
    TState,
    TAccountSettings
  >[] = [];
  private dashboardWidgetBindings: readonly BoundDashboardWidget<
    AnyDashboardWidgetDefinition,
    z.output<TConfigSchema>,
    TState,
    TAccountSettings
  >[] = [];
  private scopedShell: IShell | undefined;
  private state: TState | undefined;
  private tools: Tool[] | undefined;
  private resources: Resource[] | undefined;

  constructor(
    definition: NormalizedServiceDefinitionInput<
      TConfigSchema,
      TState,
      TPromptSchemas,
      TTemplateSchemas,
      TViewSchemas,
      TAccountSettings,
      TTemplateDefinitions
    >,
    config: z.output<TConfigSchema>,
    metadata: InstalledPluginPackageMetadata,
    id: string,
  ) {
    super(id, metadata, config, identityConfigSchema());
    this.definition = definition;
    this.publicId = definition.id;
  }

  public override register(
    shell: IShell,
    registrationContext?: PluginRegistrationContext,
  ): Promise<PluginCapabilities> {
    this.scopedShell = shell;
    return super.register(shell, registrationContext);
  }

  protected override async onRegister(
    context: ServicePluginContext,
  ): Promise<void> {
    await super.onRegister(context);
    if (this.definition.accountSettings && !context.executionOnly) {
      this.accountSettingsRegistration = context.accountSettings.register({
        ownerPluginId: this.id,
        packageName: this.packageName,
        definitionId: this.definition.id,
        definition: this.definition.accountSettings,
      });
    }
    this.state = this.definition.setup
      ? await this.definition.setup({
          config: this.config,
          lifecycle: {
            onCleanup: (cleanup): void => {
              this.cleanups.push(cleanup);
            },
          },
        })
      : emptyPluginState<TState>();

    const templates = this.templateFormatter();
    context.templates.register(this.runtimeTemplates(), this.id);
    this.registerPrompts();

    const bindings =
      this.definition.jobs?.({ config: this.config, state: this.state }) ?? [];
    const names = new Set<string>();
    for (const binding of bindings) {
      const job = binding.definition;
      if (names.has(job.name)) {
        throw new Error(
          `Service "${this.publicId}" registers job "${job.name}" more than once`,
        );
      }
      names.add(job.name);
      this.registeredJobs.add(job);
      bindServiceJobRuntimeType(job, `${this.id}:${job.name}`);
      context.jobs.registerHandler(
        job.name,
        runtimeJobHandler(binding, context, templates),
      );
    }
  }

  protected override async onRegistrationComplete(
    context: ServicePluginContext,
  ): Promise<void> {
    if (
      this.definition.accountSettings &&
      !context.executionOnly &&
      !context.accountSettings.hasBackend()
    ) {
      throw new Error(
        `Service "${this.publicId}" account settings require auth-service and an account settings encryption key`,
      );
    }
    if (context.executionOnly) return;
    this.bindOperatorDefinitions(context);

    const acquiredStudio: string[] = [];
    const acquiredDashboard: string[] = [];
    try {
      for (const binding of this.studioWorkspaceBindings) {
        const runtimeWorkspaceId = `${this.id}:${binding.definition.id}`;
        const registration = createDeclarativeStudioWorkspaceRegistration({
          publicServiceId: this.publicId,
          packageName: this.packageName,
          runtimeWorkspaceId,
          config: this.config,
          state: this.requireState(),
          ...(this.accountSettingsRegistration
            ? {
                accountSettingsRegistration: this.accountSettingsRegistration,
              }
            : {}),
          binding,
          context,
          runtimeSignal: this.operatorAbortController.signal,
        });
        try {
          const result = await context.studio.registerWorkspace(registration);
          if (result === false) {
            await this.rollbackStudioWorkspaces(context, acquiredStudio);
            acquiredStudio.splice(0);
            break;
          }
        } catch (error) {
          throw new Error(
            `Service "${this.publicId}" package "${this.packageName}" Studio workspace "${binding.definition.id}" host registration failed; correct the declaration or Studio configuration: ${getErrorMessage(error)}`,
            { cause: error },
          );
        }
        acquiredStudio.push(runtimeWorkspaceId);
      }
      this.registeredStudioWorkspaceIds.push(...acquiredStudio);

      for (const binding of this.dashboardWidgetBindings) {
        const registration = createDeclarativeDashboardWidgetRegistration({
          publicServiceId: this.publicId,
          packageName: this.packageName,
          config: this.config,
          state: this.requireState(),
          ...(this.accountSettingsRegistration
            ? {
                accountSettingsRegistration: this.accountSettingsRegistration,
              }
            : {}),
          binding,
          context,
          runtimeSignal: this.operatorAbortController.signal,
        });
        try {
          const registered =
            await context.dashboard.registerWidget(registration);
          if (!registered) {
            await this.rollbackDashboardWidgets(context, acquiredDashboard);
            acquiredDashboard.splice(0);
            break;
          }
        } catch (error) {
          throw new Error(
            `Service "${this.publicId}" package "${this.packageName}" dashboard widget "${binding.definition.id}" host registration failed; correct the declaration or Dashboard configuration: ${getErrorMessage(error)}`,
            { cause: error },
          );
        }
        acquiredDashboard.push(binding.definition.id);
      }
      this.registeredDashboardWidgetIds.push(...acquiredDashboard);
    } catch (error) {
      await this.rollbackDashboardWidgets(context, acquiredDashboard);
      await this.rollbackStudioWorkspaces(context, acquiredStudio);
      throw error;
    }
  }

  protected override async getTools(): Promise<Tool[]> {
    if (this.tools) return this.tools;
    const state = this.requireState();
    const definitions =
      this.definition.tools?.({
        config: this.config,
        state,
        jobs: this.jobs(),
        templates: this.templateFormatter(),
        content: this.content(),
      }) ?? [];
    const names = new Set<string>();
    this.tools = definitions.map((definition) => {
      if (names.has(definition.name)) {
        throw new Error(
          `Service "${this.publicId}" defines tool "${definition.name}" more than once`,
        );
      }
      names.add(definition.name);
      return this.runtimeTool(definition);
    });
    return this.tools;
  }

  protected override async getResources(): Promise<Resource[]> {
    if (this.resources) return this.resources;
    const definitions =
      this.definition.resources?.({
        config: this.config,
        state: this.requireState(),
      }) ?? {};
    this.resources = Object.entries(definitions).map(([name, definition]) =>
      this.runtimeResource(name, definition),
    );
    return this.resources;
  }

  protected override async getInstructions(): Promise<string | undefined> {
    return this.definition.instructions?.({
      config: this.config,
      state: this.requireState(),
    });
  }

  protected override async onShutdown(): Promise<void> {
    this.operatorAbortController.abort(
      new Error(`Service "${this.publicId}" is shutting down`),
    );
    await this.rollbackDashboardWidgets(
      this.getContext(),
      this.registeredDashboardWidgetIds.splice(0),
    );
    await this.rollbackStudioWorkspaces(
      this.getContext(),
      this.registeredStudioWorkspaceIds.splice(0),
    );
    this.studioWorkspaceBindings = [];
    this.dashboardWidgetBindings = [];
    this.tools = undefined;
    this.resources = undefined;
    for (const job of this.registeredJobs) {
      unbindServiceJobRuntimeType(job, `${this.id}:${job.name}`);
    }
    this.registeredJobs.clear();
    for (const cleanup of this.cleanups.splice(0).reverse()) {
      await cleanup();
    }
  }

  private bindOperatorDefinitions(context: ServicePluginContext): void {
    if (this.definition.dashboardWidgets && context.dashboard.isAvailable()) {
      const bindings = this.definition.dashboardWidgets({
        config: this.config,
        state: this.requireState(),
        accountSettings: this.definition.accountSettings,
      });
      const ids = new Set<string>();
      for (const binding of bindings) {
        const id = binding.definition.id;
        if (ids.has(id)) {
          throw new Error(
            `Service "${this.publicId}" package "${this.packageName}" registers dashboard widget "${id}" more than once; return each local widget definition once`,
          );
        }
        ids.add(id);
      }
      this.dashboardWidgetBindings = Object.freeze([...bindings]);
    }

    if (this.definition.studioWorkspaces && context.studio.isAvailable()) {
      const bindings = this.definition.studioWorkspaces({
        config: this.config,
        state: this.requireState(),
        accountSettings: this.definition.accountSettings,
      });
      const ids = new Set<string>();
      for (const binding of bindings) {
        const id = binding.definition.id;
        if (ids.has(id)) {
          throw new Error(
            `Service "${this.publicId}" package "${this.packageName}" registers Studio workspace "${id}" more than once; return each local workspace definition once`,
          );
        }
        ids.add(id);
      }
      this.studioWorkspaceBindings = Object.freeze([...bindings]);
    }
  }

  private async rollbackStudioWorkspaces(
    context: ServicePluginContext,
    workspaceIds: readonly string[],
  ): Promise<void> {
    for (const workspaceId of [...workspaceIds].reverse()) {
      try {
        await context.studio.unregisterWorkspace(workspaceId);
      } catch (error) {
        this.logger.error("Failed to unregister declarative Studio workspace", {
          serviceId: this.publicId,
          packageName: this.packageName,
          workspaceId,
          error: getErrorMessage(error),
        });
      }
      const index = this.registeredStudioWorkspaceIds.lastIndexOf(workspaceId);
      if (index >= 0) this.registeredStudioWorkspaceIds.splice(index, 1);
    }
  }

  private async rollbackDashboardWidgets(
    context: ServicePluginContext,
    widgetIds: readonly string[],
  ): Promise<void> {
    for (const widgetId of [...widgetIds].reverse()) {
      try {
        await context.dashboard.unregisterWidget(widgetId);
      } catch (error) {
        this.logger.error("Failed to unregister declarative dashboard widget", {
          serviceId: this.publicId,
          packageName: this.packageName,
          widgetId,
          error: getErrorMessage(error),
        });
      }
      const index = this.registeredDashboardWidgetIds.lastIndexOf(widgetId);
      if (index >= 0) this.registeredDashboardWidgetIds.splice(index, 1);
    }
  }

  private requireState(): TState {
    if (this.state === undefined) {
      throw new Error(`Service "${this.publicId}" has not completed setup`);
    }
    return this.state;
  }

  private jobs(): ServiceJobs {
    const context = this.getContext();
    return {
      enqueue: async <TDefinition extends AnyServiceJobDefinition>(
        definition: TDefinition,
        input: z.input<TDefinition["input"]>,
      ): Promise<ServiceJobReference<TDefinition>> => {
        if (!this.registeredJobs.has(definition)) {
          throw new Error(
            `Service "${this.publicId}" cannot enqueue unregistered job "${definition.name}"`,
          );
        }
        const data = definition.input.parse(input);
        const toolContext = this.toolContext.getStore();
        const maxRetries = definition.retry
          ? definition.retry.attempts - 1
          : undefined;
        const id = await context.jobs.enqueue({
          type: definition.name,
          data,
          ...(toolContext ? { toolContext } : {}),
          options: {
            source: this.id,
            metadata: {
              operationType: "data_processing",
              pluginId: this.id,
            },
            ...(maxRetries !== undefined ? { maxRetries } : {}),
          },
        });
        return Object.freeze({
          id,
          status: async () => {
            const job = await context.jobs.getStatus(id);
            return job ? statusFor(definition, job) : null;
          },
        });
      },
      async status<TDefinition extends AnyServiceJobDefinition>(
        definition: TDefinition,
        id: string,
      ): Promise<ServiceJobStatus<z.output<TDefinition["output"]>> | null> {
        const job = await context.jobs.getStatus(id);
        return job ? statusFor(definition, job) : null;
      },
    };
  }

  private content(): ServiceContentGeneration<string> {
    const context = this.getContext();
    const generationTemplates = this.erasedTemplates();
    const canGenerate = (name: string): boolean =>
      generationTemplates.get(name)?.generation !== undefined;
    return {
      target: (input) => createServiceContentTarget(input, canGenerate),
      generate: async (input): Promise<ServiceContentGenerationResult> => {
        const toolContext = this.toolContext.getStore();
        const result = await context.content.generate({
          targets: [...input.targets],
          toolContext,
          ...(input.force !== undefined && { force: input.force }),
          ...(input.dryRun !== undefined && { dryRun: input.dryRun }),
          ...(toolContext?.signal && { signal: toolContext.signal }),
        });
        // Authors see their local template keys; the result is built typed,
        // and the public schema validates tool output, not this producer.
        return {
          ...result,
          items: result.items.map(
            ({ destination, templateName, ...decision }) => ({
              // The stored ID travels with the path so authors read output
              // back through entity readers without rebuilding an identifier.
              destination: {
                entityType: destination.entityType,
                idPath: destination.idPath,
                entityId: destination.entityId,
              },
              template: unscopeTemplateName(templateName, this.id),
              ...decision,
            }),
          ),
        };
      },
    };
  }

  private templateFormatter(): ServiceTemplateFormatter {
    const formatters = this.erasedTemplates();
    return {
      format(name, value): string {
        const template = formatters.get(name);
        if (!template) throw new Error(`Template not found: ${name}`);
        return template.format(value);
      },
    };
  }

  /**
   * Erase each template's schema type where it is still known, binding format
   * to the schema it belongs to. Reading the definition's mapped type as a
   * plain record is not assignable — ServiceTemplateDefinition is
   * contravariant in its schema through `format` — so the erasure happens here
   * rather than being asserted away at the read site.
   */
  private erasedTemplateCache: Map<string, ErasedServiceTemplate> | undefined;

  /** Built once per plugin instance; the definition never changes after construction. */
  private erasedTemplates(): Map<string, ErasedServiceTemplate> {
    this.erasedTemplateCache ??= new Map(
      Object.entries(this.definition.templates ?? {}).map(
        ([name, template]) => [
          name,
          {
            schema: template.schema,
            generation: template.generation,
            format: (value: unknown): string =>
              template.format({ value: template.schema.parse(value) }),
          },
        ],
      ),
    );
    return this.erasedTemplateCache;
  }

  private erasedViews(): Map<string, ErasedServiceView> {
    return new Map(
      Object.entries(this.definition.views ?? {}).map(([name, view]) => [
        name,
        {
          schema: view.schema,
          description: view.description,
          component: (props: JsonObject) =>
            view.renderers.web(view.schema.parse(props)),
        },
      ]),
    );
  }

  private runtimeTemplates(): Record<string, Template> {
    const templates = this.erasedTemplates();
    const views = this.erasedViews();
    const result: Record<string, Template> = {};
    const names = new Set([...templates.keys(), ...views.keys()]);

    for (const name of names) {
      const template = templates.get(name);
      const view = views.get(name);
      if (template && view && template.schema !== view.schema) {
        throw new Error(
          `Service "${this.publicId}" template and view "${name}" must share one schema`,
        );
      }
      const schema = template?.schema ?? view?.schema;
      if (!schema) continue;
      if (template?.generation && !template.generation.prompt.trim()) {
        throw new Error(
          `Service "${this.publicId}" generation template "${name}" requires a non-empty prompt`,
        );
      }
      const base = {
        name,
        description: view?.description ?? `${this.publicId} ${name}`,
        schema,
        requiredPermission: "admin" as const,
        ...(template
          ? {
              formatter: {
                format: (value: unknown): string => template.format(value),
                parse: (): never => {
                  throw new Error(`Template "${name}" is format-only`);
                },
              },
              ...(template.generation
                ? {
                    basePrompt: template.generation.prompt,
                    dataSourceId: "shell:ai-content",
                    ...(template.generation.useKnowledgeContext !== undefined
                      ? {
                          useKnowledgeContext:
                            template.generation.useKnowledgeContext,
                        }
                      : {}),
                  }
                : {}),
            }
          : {}),
      };
      if (!view) {
        result[name] = createTemplate(base);
        continue;
      }
      result[name] = createTemplate<JsonObject>({
        ...base,
        schema: view.schema,
        layout: { component: view.component },
      });
    }
    return result;
  }

  private registerPrompts(): void {
    const shell = this.scopedShell;
    if (!shell) throw new Error(`Service "${this.publicId}" has no shell`);
    for (const [name, definition] of Object.entries(
      this.definition.prompts ?? {},
    )) {
      const prompt: Prompt = {
        name: `${this.publicId}_${name}`,
        ...(definition.description
          ? { description: definition.description }
          : {}),
        args: {
          input: {
            description: "JSON input",
            required: true,
          },
        },
        handler: async (args) => {
          const input = definition.input.parse(promptInput(args["input"]));
          return {
            messages: [
              {
                role: "user",
                content: {
                  type: "text",
                  text: definition.render({ input }),
                },
              },
            ],
          };
        },
      };
      shell.registerPrompt(this.id, prompt);
    }
  }

  private runtimeResource(
    name: string,
    definition: ServiceResourceDefinition,
  ): Resource {
    return {
      uri: definition.uri,
      name,
      ...(definition.description
        ? { description: definition.description }
        : {}),
      mimeType: definition.mimeType ?? "text/plain",
      handler: async () => ({
        contents: [
          {
            uri: definition.uri,
            mimeType: definition.mimeType ?? "text/plain",
            text: await definition.read(),
          },
        ],
      }),
    };
  }

  private runtimeTool(definition: AnyServiceToolDefinition): Tool {
    const name = `${this.publicId}_${definition.name}`;
    const confirmations = createConfirmationGate({
      label: definition.name,
      requestNoun: "the operation",
    });
    return {
      name,
      description: definition.description,
      inputSchema: definition.input.shape,
      outputSchema: definition.output,
      visibility: definition.permission ?? "admin",
      sideEffects:
        definition.sideEffects ?? (definition.confirmation ? "writes" : "none"),
      handler: async (rawInput, toolContext): Promise<ToolResponse> => {
        try {
          const token = toolConfirmationToken(rawInput);
          if (token !== undefined) {
            const gateError = confirmations.validateConfirmed(token, rawInput);
            if (gateError) return gateError;
            const record = {
              ...z.record(z.string(), z.unknown()).parse(rawInput),
            };
            delete record[confirmationTokenField];
            rawInput = record;
          }
          const input = definition.input.parse(rawInput);
          if (token === undefined && definition.confirmation) {
            return {
              needsConfirmation: true,
              toolName: name,
              summary: definition.confirmation,
              args: confirmations.buildArgs((confirmationToken) => ({
                ...z.record(z.string(), z.unknown()).parse(input),
                [confirmationTokenField]: confirmationToken,
              })),
            };
          }

          const output = await this.toolContext.run(toolContext, () =>
            definition.execute({
              input,
              signal: toolContext.signal ?? new AbortController().signal,
            }),
          );
          return {
            success: true,
            data: definition.output.parse(output),
          };
        } catch (error) {
          return { success: false, error: getErrorMessage(error) };
        }
      },
    };
  }
}

export function createDeclarativeServicePlugin<
  TConfigSchema extends z.ZodType<object, object>,
  TState extends object,
  TPromptSchemas extends ServiceSchemaMap,
  TTemplateSchemas extends ServiceSchemaMap,
  TViewSchemas extends ServiceViewSchemaMap,
  TAccountSettings extends AnyAccountSettingsDefinition | undefined,
  TTemplateDefinitions extends ServiceTemplateShapeMap,
>(
  definition: NormalizedServiceDefinitionInput<
    TConfigSchema,
    TState,
    TPromptSchemas,
    TTemplateSchemas,
    TViewSchemas,
    TAccountSettings,
    TTemplateDefinitions
  >,
  config: z.output<TConfigSchema>,
  metadata: InstalledPluginPackageMetadata,
  id: string,
): DeclarativeServicePlugin<
  TConfigSchema,
  TState,
  TPromptSchemas,
  TTemplateSchemas,
  TViewSchemas,
  TAccountSettings,
  TTemplateDefinitions
> {
  return new DeclarativeServicePlugin(definition, config, metadata, id);
}
